interface BillingIdentitySnapshot {
  providerType: string;
  legalName: string;
  tradingName: string;
  businessName: string;
  abn: string;
  invoiceEmail: string;
  phone: string;
  address: string;
  gstRegistered: string;
  paymentAccountName: string;
  paymentBSB: string;
  paymentAccountNumber: string;
  paymentReferencePreference: string;
  paymentReferenceCustom: string;
  paymentNotes: string;
  snapshotAt: string;
}

interface ReviewParticipant {
  name: string;
  ndisNumber: string;
  billingType: string;
  planManagerName: string;
  planManagerEmail: string;
  address: string;
}

interface ReviewProvider {
  providerType?: string;
  businessName: string;
  supportWorkerName: string;
  abn: string;
  invoiceEmail: string;
  phone: string;
  address: string;
  gstRegistered: string;
  paymentAccountName?: string;
  paymentBSB?: string;
  paymentAccountNumber?: string;
  paymentReferencePreference?: string;
  paymentReferenceCustom?: string;
  paymentNotes?: string;
  paymentDetailsPlaceholder?: string;
}

interface ReviewSession {
  participantName: string;
  providerName: string;
}

interface ReviewLineItem {
  supportItemCode: string;
  supportItemName: string;
  date: string;
  startTime: string;
  endTime: string;
  quantity: number;
  unit: string;
  rate: number;
  gstComponent: number;
}

interface ReviewInvoiceDraft {
  invoiceNumber: string;
  invoiceDate: string;
  serviceDate: string;
  dueDate: string;
  status: string;
  participant: ReviewParticipant;
  provider: ReviewProvider;
  session: ReviewSession;
  lineItems: ReviewLineItem[];
  billingSnapshot?: BillingIdentitySnapshot;
  providerReviewStatus?: 'provider-confirmed';
  providerConfirmedAt?: string;
}

interface AuditEvent {
  id: string;
  at: string;
  label: string;
  detail: string;
}

type ApplyScope = 'invoice-only' | 'profile-future' | 'both';

const REVIEW_STORAGE_KEY = 'hub-invoice-review-draft';
const INVOICE_STORAGE_KEY = 'hub-invoice-draft';
const PROVIDER_STORAGE_KEY = 'hub-provider-draft';
const AUDIT_STORAGE_KEY = 'hub-invoice-review-audit';
const GENERATED_FOOTER_TEXT = 'Generated By; The Hub Ecosystems';

let reviewDraft: ReviewInvoiceDraft = defaultReviewDraft();
let identityDraft: BillingIdentitySnapshot = defaultSnapshot();
let applyScope: ApplyScope = 'invoice-only';
let issuePanelOpen = false;
let issueCategory = 'provider details wrong';
let issueDescription = '';
let noticeText = '';

function defaultSnapshot(): BillingIdentitySnapshot {
  return {
    providerType: 'individual',
    legalName: '',
    tradingName: '',
    businessName: '',
    abn: '',
    invoiceEmail: '',
    phone: '',
    address: '',
    gstRegistered: 'unknown',
    paymentAccountName: '',
    paymentBSB: '',
    paymentAccountNumber: '',
    paymentReferencePreference: 'invoice-number',
    paymentReferenceCustom: '',
    paymentNotes: '',
    snapshotAt: '',
  };
}

function defaultReviewDraft(): ReviewInvoiceDraft {
  return {
    invoiceNumber: '',
    invoiceDate: '',
    serviceDate: '',
    dueDate: '',
    status: 'Draft',
    participant: { name: '', ndisNumber: '', billingType: 'unknown', planManagerName: '', planManagerEmail: '', address: '' },
    provider: { businessName: '', supportWorkerName: '', abn: '', invoiceEmail: '', phone: '', address: '', gstRegistered: 'unknown' },
    session: { participantName: '', providerName: '' },
    lineItems: [],
  };
}

function injectStyles(): void {
  if (document.getElementById('provider-invoice-review-styles')) return;
  const style = document.createElement('style');
  style.id = 'provider-invoice-review-styles';
  style.textContent = `
body.sp-public-body { display: block !important; min-height: 100dvh; height: auto !important; overflow-y: auto !important; background: var(--bg) !important; }
.sp-public-root { display: block !important; width: 100% !important; min-height: 100dvh !important; height: auto !important; overflow: visible !important; flex: none !important; background: var(--bg); }
.pir-page { min-height: 100dvh; width: 100%; box-sizing: border-box; background: var(--bg); color: var(--fg); font-family: var(--font-sans); padding: 24px; }
.pir-shell { width: 100%; max-width: 1220px; margin: 0 auto; }
.pir-topbar { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; margin-bottom: 18px; padding-bottom: 16px; border-bottom: 1px solid var(--border); }
.pir-brand { color: var(--fg-muted); font-size: 12px; font-weight: 700; text-transform: uppercase; }
.pir-header h1 { margin: 6px 0 6px; color: var(--fg); font-size: 24px; line-height: 1.2; font-weight: 800; }
.pir-header p { margin: 0; color: var(--fg-muted); font-size: 14px; line-height: 1.45; }
.pir-badges { display: flex; justify-content: flex-end; gap: 8px; flex-wrap: wrap; }
.pir-badge { display: inline-flex; align-items: center; min-height: 24px; padding: 3px 9px; border-radius: 4px; border: 1px solid var(--warning); background: var(--bg-secondary); color: var(--warning); font-size: 11px; font-weight: 700; text-transform: uppercase; }
.pir-grid { display: grid; grid-template-columns: minmax(0, 1.25fr) minmax(320px, 0.75fr); gap: 14px; align-items: start; }
.pir-card { border: 1px solid var(--border); border-radius: 8px; background: var(--bg-secondary); padding: 14px; margin-bottom: 14px; }
.pir-card h2 { margin: 0 0 12px; color: var(--fg); font-size: 17px; line-height: 1.25; }
.pir-preview { position: relative; border: 1px solid var(--border); border-radius: 8px; background: var(--bg); color: var(--fg); padding: 18px; overflow: hidden; }
.pir-watermark { position: absolute; inset: 42% auto auto 10%; transform: rotate(-18deg); color: var(--fg-muted); font-size: 32px; font-weight: 800; opacity: 0.28; pointer-events: none; }
.pir-preview-head { display: flex; justify-content: space-between; gap: 16px; margin-bottom: 18px; position: relative; }
.pir-preview-title { margin: 0; color: var(--fg); font-size: 26px; line-height: 1.15; }
.pir-preview-meta { min-width: 230px; text-align: right; }
.pir-party-row { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 28px; margin-bottom: 16px; padding-bottom: 14px; border-bottom: 1px solid var(--border); position: relative; }
.pir-label { color: var(--fg-muted); font-size: 11px; font-weight: 800; text-transform: uppercase; }
.pir-block { color: var(--fg); font-size: 13px; line-height: 1.5; white-space: pre-line; }
.pir-table-wrap { overflow-x: auto; position: relative; }
.pir-table { width: 100%; min-width: 760px; border-collapse: collapse; margin: 16px 0; font-size: 12px; color: var(--fg); }
.pir-table th, .pir-table td { padding: 8px; border-bottom: 1px solid var(--border); text-align: left; vertical-align: top; }
.pir-table th { color: var(--fg-muted); background: var(--bg-secondary); text-transform: uppercase; font-size: 11px; }
.pir-totals { display: grid; gap: 8px; max-width: 360px; margin-left: auto; }
.pir-total-row { display: flex; justify-content: space-between; gap: 16px; color: var(--fg); font-size: 14px; }
.pir-payto { margin-top: 18px; padding-top: 14px; border-top: 1px dotted var(--border); position: relative; }
.pir-preview-footer { display: flex; justify-content: space-between; gap: 16px; margin-top: 18px; padding-top: 10px; border-top: 1px solid var(--border); color: var(--fg-muted); font-size: 11px; font-weight: 800; }
.pir-fields { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }
.pir-field { min-width: 0; }
.pir-field-wide { grid-column: 1 / -1; }
.pir-field span { display: block; margin-bottom: 5px; color: var(--fg-muted); font-size: 12px; font-weight: 700; }
.pir-input, .pir-select, .pir-textarea { width: 100%; min-height: 38px; box-sizing: border-box; border: 1px solid var(--border); border-radius: 6px; background: var(--bg); color: var(--fg); padding: 8px 10px; font: inherit; font-size: 13px; }
.pir-textarea { min-height: 88px; resize: vertical; }
.pir-radio-list { display: grid; gap: 8px; margin: 10px 0 12px; }
.pir-radio { display: flex; gap: 8px; align-items: center; padding: 9px 10px; border: 1px solid var(--border); border-radius: 6px; background: var(--bg); color: var(--fg); font-size: 13px; font-weight: 700; }
.pir-radio input { accent-color: var(--accent); }
.pir-actions { display: flex; justify-content: flex-end; gap: 10px; flex-wrap: wrap; margin-top: 12px; }
.pir-button { min-height: 38px; border: 1px solid var(--accent); border-radius: 6px; background: var(--accent); color: var(--bg); padding: 8px 14px; font: inherit; font-size: 13px; font-weight: 800; cursor: pointer; }
.pir-button-secondary { border-color: var(--border); background: var(--bg); color: var(--fg); }
.pir-notice { margin-bottom: 14px; padding: 10px 12px; border: 1px solid var(--success); border-radius: 6px; background: var(--bg-secondary); color: var(--success); font-size: 13px; font-weight: 800; }
.pir-audit { display: grid; gap: 8px; }
.pir-audit-item { padding: 9px 10px; border: 1px solid var(--border); border-radius: 6px; background: var(--bg); color: var(--fg); font-size: 12px; line-height: 1.45; }
.pir-audit-time { color: var(--fg-muted); font-weight: 700; }
.pir-footer { margin-top: 18px; padding-top: 12px; border-top: 1px solid var(--border); color: var(--fg-muted); font-size: 12px; font-weight: 800; }
@media (max-width: 768px) {
  .pir-page { padding: 16px; }
  .pir-topbar, .pir-preview-head, .pir-preview-footer { flex-direction: column; }
  .pir-badges { justify-content: flex-start; }
  .pir-grid, .pir-party-row, .pir-fields { grid-template-columns: 1fr; }
  .pir-field-wide { grid-column: auto; }
  .pir-preview-meta { min-width: 0; text-align: left; }
  .pir-actions { justify-content: stretch; }
  .pir-button { width: 100%; }
  .pir-totals { margin-left: 0; max-width: none; }
}
`;
  document.head.appendChild(style);
}

function esc(value: string): string {
  const el = document.createElement('div');
  el.textContent = value;
  return el.innerHTML;
}

function shell(): HTMLElement {
  document.title = 'Provider Invoice Review - Hub People';
  injectStyles();
  document.body.classList.add('sp-public-body');
  const root = (document.getElementById('app') ?? document.body) as HTMLElement;
  root.classList.add('sp-public-root');
  root.innerHTML = '';
  for (const id of ['sidebar-scrim', 'modal-overlay', 'toast']) {
    const el = document.getElementById(id);
    if (el) el.style.display = 'none';
  }
  const page = document.createElement('div');
  page.className = 'pir-page';
  root.appendChild(page);
  return page;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function stringField(source: Record<string, unknown>, key: string): string {
  const value = source[key];
  return typeof value === 'string' ? value : '';
}

function numberField(source: Record<string, unknown>, key: string): number {
  const value = source[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function readInvoice(): ReviewInvoiceDraft {
  const raw = localStorage.getItem(REVIEW_STORAGE_KEY) ?? localStorage.getItem(INVOICE_STORAGE_KEY);
  if (!raw) return defaultReviewDraft();
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!isRecord(parsed)) return defaultReviewDraft();
    const fallback = defaultReviewDraft();
    const participantSource = isRecord(parsed['participant']) ? parsed['participant'] : {};
    const providerSource = isRecord(parsed['provider']) ? parsed['provider'] : {};
    const sessionSource = isRecord(parsed['session']) ? parsed['session'] : {};
    const lineItems = Array.isArray(parsed['lineItems'])
      ? parsed['lineItems'].filter(isRecord).map(item => ({
        supportItemCode: stringField(item, 'supportItemCode'),
        supportItemName: stringField(item, 'supportItemName'),
        date: stringField(item, 'date'),
        startTime: stringField(item, 'startTime'),
        endTime: stringField(item, 'endTime'),
        quantity: numberField(item, 'quantity'),
        unit: stringField(item, 'unit'),
        rate: numberField(item, 'rate'),
        gstComponent: numberField(item, 'gstComponent'),
      }))
      : [];
    const snapshot = isRecord(parsed['billingSnapshot']) ? { ...defaultSnapshot(), ...parsed['billingSnapshot'] } as BillingIdentitySnapshot : undefined;
    return {
      ...fallback,
      invoiceNumber: stringField(parsed, 'invoiceNumber'),
      invoiceDate: stringField(parsed, 'invoiceDate'),
      serviceDate: stringField(parsed, 'serviceDate'),
      dueDate: stringField(parsed, 'dueDate'),
      status: stringField(parsed, 'status') || 'Draft',
      participant: {
        name: stringField(participantSource, 'name'),
        ndisNumber: stringField(participantSource, 'ndisNumber'),
        billingType: stringField(participantSource, 'billingType') || 'unknown',
        planManagerName: stringField(participantSource, 'planManagerName'),
        planManagerEmail: stringField(participantSource, 'planManagerEmail'),
        address: stringField(participantSource, 'address'),
      },
      provider: {
        providerType: stringField(providerSource, 'providerType'),
        businessName: stringField(providerSource, 'businessName'),
        supportWorkerName: stringField(providerSource, 'supportWorkerName'),
        abn: stringField(providerSource, 'abn'),
        invoiceEmail: stringField(providerSource, 'invoiceEmail'),
        phone: stringField(providerSource, 'phone'),
        address: stringField(providerSource, 'address'),
        gstRegistered: stringField(providerSource, 'gstRegistered') || 'unknown',
        paymentAccountName: stringField(providerSource, 'paymentAccountName'),
        paymentBSB: stringField(providerSource, 'paymentBSB'),
        paymentAccountNumber: stringField(providerSource, 'paymentAccountNumber'),
        paymentReferencePreference: stringField(providerSource, 'paymentReferencePreference') || 'invoice-number',
        paymentReferenceCustom: stringField(providerSource, 'paymentReferenceCustom'),
        paymentNotes: stringField(providerSource, 'paymentNotes'),
        paymentDetailsPlaceholder: stringField(providerSource, 'paymentDetailsPlaceholder'),
      },
      session: {
        participantName: stringField(sessionSource, 'participantName'),
        providerName: stringField(sessionSource, 'providerName'),
      },
      lineItems,
      billingSnapshot: snapshot,
      providerReviewStatus: stringField(parsed, 'providerReviewStatus') === 'provider-confirmed' ? 'provider-confirmed' : undefined,
      providerConfirmedAt: stringField(parsed, 'providerConfirmedAt'),
    };
  } catch {
    return defaultReviewDraft();
  }
}

function snapshotFromInvoice(invoice: ReviewInvoiceDraft): BillingIdentitySnapshot {
  const providerType = invoice.billingSnapshot?.providerType || invoice.provider.providerType || 'individual';
  return {
    ...defaultSnapshot(),
    ...invoice.billingSnapshot,
    providerType,
    legalName: invoice.billingSnapshot?.legalName || (providerType === 'company' || providerType === 'organisation' ? invoice.provider.businessName : invoice.provider.supportWorkerName),
    tradingName: invoice.billingSnapshot?.tradingName || (providerType === 'sole-trader-trading' ? invoice.provider.businessName : ''),
    businessName: invoice.billingSnapshot?.businessName || invoice.provider.businessName,
    abn: invoice.billingSnapshot?.abn || invoice.provider.abn,
    invoiceEmail: invoice.billingSnapshot?.invoiceEmail || invoice.provider.invoiceEmail,
    phone: invoice.billingSnapshot?.phone || invoice.provider.phone,
    address: invoice.billingSnapshot?.address || invoice.provider.address,
    gstRegistered: invoice.billingSnapshot?.gstRegistered || invoice.provider.gstRegistered,
    paymentAccountName: invoice.billingSnapshot?.paymentAccountName || invoice.provider.paymentAccountName || '',
    paymentBSB: invoice.billingSnapshot?.paymentBSB || invoice.provider.paymentBSB || '',
    paymentAccountNumber: invoice.billingSnapshot?.paymentAccountNumber || invoice.provider.paymentAccountNumber || '',
    paymentReferencePreference: invoice.billingSnapshot?.paymentReferencePreference || invoice.provider.paymentReferencePreference || 'invoice-number',
    paymentReferenceCustom: invoice.billingSnapshot?.paymentReferenceCustom || invoice.provider.paymentReferenceCustom || '',
    paymentNotes: invoice.billingSnapshot?.paymentNotes || invoice.provider.paymentNotes || '',
    snapshotAt: invoice.billingSnapshot?.snapshotAt || new Date().toISOString(),
  };
}

function saveReviewDraft(): void {
  reviewDraft.billingSnapshot = { ...identityDraft, snapshotAt: new Date().toISOString() };
  localStorage.setItem(REVIEW_STORAGE_KEY, JSON.stringify(reviewDraft));
}

function readAudit(): AuditEvent[] {
  try {
    const raw = localStorage.getItem(AUDIT_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isRecord).map(item => ({
      id: stringField(item, 'id'),
      at: stringField(item, 'at'),
      label: stringField(item, 'label'),
      detail: stringField(item, 'detail'),
    })).filter(item => item.id && item.at && item.label);
  } catch {
    return [];
  }
}

function writeAudit(events: AuditEvent[]): void {
  localStorage.setItem(AUDIT_STORAGE_KEY, JSON.stringify(events));
}

function addAudit(label: string, detail: string): void {
  const events = readAudit();
  events.push({ id: crypto.randomUUID(), at: new Date().toISOString(), label, detail });
  writeAudit(events);
}

function ensureAuditSeed(): void {
  const events = readAudit();
  if (events.length > 0) return;
  const now = new Date().toISOString();
  writeAudit([
    { id: crypto.randomUUID(), at: identityDraft.snapshotAt || now, label: 'Review link generated', detail: 'Provider review draft created in localStorage.' },
    { id: crypto.randomUUID(), at: now, label: 'Email sent (test)', detail: 'Mitchell-only provider review test email recorded.' },
  ]);
}

function money(value: number): string {
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(Number.isFinite(value) ? value : 0);
}

function lineTotal(item: ReviewLineItem): number {
  return item.quantity * item.rate + item.gstComponent;
}

function subtotal(): number {
  return reviewDraft.lineItems.reduce((sum, item) => sum + item.quantity * item.rate, 0);
}

function gstTotal(): number {
  return reviewDraft.lineItems.reduce((sum, item) => sum + item.gstComponent, 0);
}

function grandTotal(): number {
  return subtotal() + gstTotal();
}

function participantName(): string {
  return reviewDraft.participant.name || reviewDraft.session.participantName || 'Participant not entered';
}

function providerName(): string {
  if (identityDraft.providerType === 'company' || identityDraft.providerType === 'organisation') {
    return identityDraft.businessName || identityDraft.legalName || 'Provider not entered';
  }
  return identityDraft.legalName || identityDraft.businessName || reviewDraft.session.providerName || 'Provider not entered';
}

function providerLines(): string[] {
  return [
    identityDraft.tradingName ? `Trading as: ${identityDraft.tradingName}` : '',
    identityDraft.providerType === 'company' || identityDraft.providerType === 'organisation' ? (identityDraft.legalName && identityDraft.legalName !== identityDraft.businessName ? `Contact: ${identityDraft.legalName}` : '') : '',
    identityDraft.abn ? `ABN ${identityDraft.abn}` : '',
    identityDraft.invoiceEmail,
    identityDraft.phone,
    identityDraft.address,
  ].filter(line => line.trim().length > 0);
}

function billToLines(): string[] {
  return [
    participantName(),
    `NDIS/reference: ${reviewDraft.participant.ndisNumber || 'Not entered'}`,
    `Billing type: ${reviewDraft.participant.billingType || 'unknown'}`,
    reviewDraft.participant.billingType === 'plan-managed' ? `Plan manager: ${reviewDraft.participant.planManagerName || 'Not entered'}` : '',
    reviewDraft.participant.billingType === 'plan-managed' ? `Plan manager email: ${reviewDraft.participant.planManagerEmail || 'Not entered'}` : '',
    reviewDraft.participant.address,
  ].filter(line => line.trim().length > 0);
}

function timeRange(item: ReviewLineItem): string {
  if (item.startTime && item.endTime) return `${item.startTime}-${item.endTime}`;
  if (item.startTime) return `${item.startTime}-`;
  return '-';
}

function paymentLines(): string[] {
  const lines = [
    identityDraft.paymentAccountName ? `Account Name: ${identityDraft.paymentAccountName}` : '',
    identityDraft.paymentBSB ? `BSB: ${identityDraft.paymentBSB}` : '',
    identityDraft.paymentAccountNumber ? `Account Number: ${identityDraft.paymentAccountNumber}` : '',
  ].filter(line => line.trim().length > 0);
  const reference = identityDraft.paymentReferencePreference === 'custom' && identityDraft.paymentReferenceCustom
    ? identityDraft.paymentReferenceCustom
    : identityDraft.paymentReferencePreference === 'participant-name'
      ? participantName()
      : reviewDraft.invoiceNumber || 'Invoice number';
  lines.push(`Payment Reference: ${reference}`);
  if (identityDraft.paymentNotes) lines.push(identityDraft.paymentNotes);
  return lines.length > 0 ? lines : ['Payment details not entered'];
}

function renderPreviewTable(): string {
  return `
    <div class="pir-table-wrap">
      <table class="pir-table">
        <thead><tr><th>Date</th><th>Code</th><th>Description</th><th>Time</th><th>Qty</th><th>Unit</th><th>Rate</th><th>GST</th><th>Total</th></tr></thead>
        <tbody>${reviewDraft.lineItems.map(item => `
          <tr>
            <td>${esc(item.date)}</td><td>${esc(item.supportItemCode)}</td><td>${esc(item.supportItemName)}</td>
            <td>${esc(timeRange(item))}</td><td>${item.quantity.toFixed(2)}</td><td>${esc(item.unit)}</td>
            <td>${money(item.rate)}</td><td>${money(item.gstComponent)}</td><td>${money(lineTotal(item))}</td>
          </tr>
        `).join('')}</tbody>
      </table>
    </div>
  `;
}

function renderInvoicePreview(): string {
  const heading = identityDraft.gstRegistered === 'yes' ? 'TAX INVOICE' : 'INVOICE';
  const invoiceDetails = [
    `Invoice number: ${reviewDraft.invoiceNumber || 'Not entered'}`,
    `Invoice date: ${reviewDraft.invoiceDate || 'Not entered'}`,
    `Service date: ${reviewDraft.serviceDate || 'Not entered'}`,
    reviewDraft.dueDate ? `Due date: ${reviewDraft.dueDate}` : '',
  ].filter(Boolean);
  return `
    <section class="pir-card" aria-label="Invoice preview">
      <h2>Invoice Preview</h2>
      <div class="pir-preview">
        <div class="pir-watermark">DRAFT -- REVIEW REQUIRED</div>
        <div class="pir-preview-head">
          <h2 class="pir-preview-title">${esc(heading)}</h2>
          <div class="pir-preview-meta"><div class="pir-block">${invoiceDetails.map((line, index) => index === 0 ? `<strong>${esc(line)}</strong>` : esc(line)).join('\n')}</div></div>
        </div>
        <div class="pir-party-row">
          <section><div class="pir-label">FROM</div><div class="pir-block"><strong>${esc(providerName())}</strong>
${providerLines().map(esc).join('\n')}</div></section>
          <section><div class="pir-label">TO / BILL TO</div><div class="pir-block"><strong>${esc(participantName())}</strong>
${billToLines().slice(1).map(esc).join('\n')}</div></section>
        </div>
        ${renderPreviewTable()}
        <div class="pir-totals">
          <div class="pir-total-row"><span>Subtotal</span><strong>${money(subtotal())}</strong></div>
          <div class="pir-total-row"><span>GST</span><strong>${money(gstTotal())}</strong></div>
          <div class="pir-total-row"><span>Total Due</span><strong>${money(grandTotal())}</strong></div>
        </div>
        <section class="pir-payto"><h3 class="pir-label">Payment / Pay To</h3><div class="pir-block">${paymentLines().map(esc).join('\n')}</div></section>
        <footer class="pir-preview-footer"><span>${esc(GENERATED_FOOTER_TEXT)}</span><span>DRAFT -- REVIEW REQUIRED -- No NDIS claim submitted</span></footer>
      </div>
    </section>
  `;
}

function identityField(field: keyof BillingIdentitySnapshot, label: string, wide = false): string {
  return `
    <label class="pir-field${wide ? ' pir-field-wide' : ''}">
      <span>${esc(label)}</span>
      <input class="pir-input" data-identity-field="${esc(field)}" type="text" value="${esc(String(identityDraft[field]))}">
    </label>
  `;
}

function identitySelect(): string {
  const types: Array<{ value: string; label: string }> = [
    { value: 'individual', label: 'Individual / sole trader' },
    { value: 'sole-trader-trading', label: 'Sole trader with trading name' },
    { value: 'company', label: 'Company / Pty Ltd' },
    { value: 'organisation', label: 'Organisation / provider business' },
  ];
  const refs: Array<{ value: string; label: string }> = [
    { value: 'invoice-number', label: 'Invoice number' },
    { value: 'participant-name', label: 'Participant name' },
    { value: 'custom', label: 'Custom' },
  ];
  return `
    <label class="pir-field">
      <span>Provider type</span>
      <select class="pir-select" data-identity-field="providerType">${types.map(option => `<option value="${esc(option.value)}"${identityDraft.providerType === option.value ? ' selected' : ''}>${esc(option.label)}</option>`).join('')}</select>
    </label>
    <label class="pir-field">
      <span>Payment reference preference</span>
      <select class="pir-select" data-identity-field="paymentReferencePreference">${refs.map(option => `<option value="${esc(option.value)}"${identityDraft.paymentReferencePreference === option.value ? ' selected' : ''}>${esc(option.label)}</option>`).join('')}</select>
    </label>
  `;
}

function scopeOptions(): string {
  const scopes: Array<{ value: ApplyScope; label: string }> = [
    { value: 'invoice-only', label: 'Apply to this invoice only' },
    { value: 'profile-future', label: 'Apply to provider profile for future invoices' },
    { value: 'both', label: 'Apply to both' },
  ];
  return `<div class="pir-radio-list">${scopes.map(scope => `<label class="pir-radio"><input type="radio" name="applyScope" data-scope="${esc(scope.value)}"${applyScope === scope.value ? ' checked' : ''}><span>${esc(scope.label)}</span></label>`).join('')}</div>`;
}

function renderIdentityPanel(): string {
  return `
    <section class="pir-card" aria-label="Billing identity">
      <h2>Billing Identity</h2>
      <div class="pir-fields">
        ${identitySelect()}
        ${identityField('legalName', 'Legal/person name')}
        ${identityField('tradingName', 'Trading name')}
        ${identityField('businessName', 'Business name')}
        ${identityField('abn', 'ABN')}
        ${identityField('invoiceEmail', 'Email')}
        ${identityField('phone', 'Phone')}
        ${identityField('address', 'Address', true)}
        ${identityField('paymentAccountName', 'Payment account name')}
        ${identityField('paymentBSB', 'BSB')}
        ${identityField('paymentAccountNumber', 'Account number')}
        ${identityDraft.paymentReferencePreference === 'custom' ? identityField('paymentReferenceCustom', 'Custom reference') : ''}
      </div>
      ${scopeOptions()}
      <div class="pir-actions">
        <button class="pir-button pir-button-secondary" id="pir-save-identity" type="button">Save Billing Identity Changes</button>
      </div>
    </section>
  `;
}

function renderIssuePanel(): string {
  if (!issuePanelOpen) return '';
  const categories = ['provider details wrong', 'payment details wrong', 'line item wrong', 'hours/quantity wrong', 'rate/code wrong', 'other'];
  return `
    <section class="pir-card" aria-label="Report issue">
      <h2>Report Issue</h2>
      <div class="pir-fields">
        <label class="pir-field pir-field-wide"><span>Category</span><select class="pir-select" id="pir-issue-category">${categories.map(category => `<option value="${esc(category)}"${issueCategory === category ? ' selected' : ''}>${esc(category)}</option>`).join('')}</select></label>
        <label class="pir-field pir-field-wide"><span>Issue description</span><textarea class="pir-textarea" id="pir-issue-description">${esc(issueDescription)}</textarea></label>
      </div>
      <div class="pir-actions"><button class="pir-button" id="pir-submit-issue" type="button">Submit Issue</button></div>
    </section>
  `;
}

function renderAuditPanel(): string {
  const events = readAudit();
  return `
    <section class="pir-card" aria-label="Mock audit log">
      <h2>Mock Audit Log</h2>
      <div class="pir-audit">${events.map(event => `
        <div class="pir-audit-item"><div><strong>${esc(event.label)}</strong></div><div>${esc(event.detail)}</div><div class="pir-audit-time">${esc(new Date(event.at).toLocaleString('en-AU'))}</div></div>
      `).join('')}</div>
    </section>
  `;
}

function applyIdentityToProviderProfile(): void {
  let profile: Record<string, unknown> = {};
  try {
    const raw = localStorage.getItem(PROVIDER_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as unknown;
      if (isRecord(parsed)) profile = parsed;
    }
  } catch {
    profile = {};
  }
  profile['providerType'] = identityDraft.providerType;
  profile['businessName'] = identityDraft.businessName || identityDraft.tradingName;
  profile['supportWorkerName'] = identityDraft.legalName;
  profile['abn'] = identityDraft.abn;
  profile['invoiceEmail'] = identityDraft.invoiceEmail;
  profile['phone'] = identityDraft.phone;
  profile['address'] = identityDraft.address;
  profile['gstRegistered'] = identityDraft.gstRegistered;
  profile['paymentAccountName'] = identityDraft.paymentAccountName;
  profile['paymentBSB'] = identityDraft.paymentBSB;
  profile['paymentAccountNumber'] = identityDraft.paymentAccountNumber;
  profile['paymentReferencePreference'] = identityDraft.paymentReferencePreference;
  profile['paymentReferenceCustom'] = identityDraft.paymentReferenceCustom;
  profile['paymentNotes'] = identityDraft.paymentNotes;
  localStorage.setItem(PROVIDER_STORAGE_KEY, JSON.stringify(profile));
}

function renderPage(page: HTMLElement): void {
  page.innerHTML = `
    <main class="pir-shell">
      <div class="pir-topbar">
        <header class="pir-header">
          <div class="pir-brand">the hub ecosystems</div>
          <h1>Provider Invoice Review</h1>
          <p>Review invoice details before any downstream billing action.</p>
        </header>
        <div class="pir-badges">
          <span class="pir-badge">Test only</span>
          <span class="pir-badge">No claim submitted</span>
          <span class="pir-badge">Not sent to participant/plan manager/NDIA</span>
        </div>
      </div>
      ${noticeText ? `<div class="pir-notice">${esc(noticeText)}</div>` : ''}
      <div class="pir-grid">
        <div>${renderInvoicePreview()}</div>
        <aside>
          ${renderIdentityPanel()}
          <section class="pir-card" aria-label="Review actions">
            <h2>Review Actions</h2>
            <div class="pir-actions">
              <button class="pir-button" id="pir-confirm" type="button">Confirm Invoice Details</button>
              <button class="pir-button pir-button-secondary" id="pir-report" type="button">Report Issue</button>
            </div>
          </section>
          ${renderIssuePanel()}
          ${renderAuditPanel()}
        </aside>
      </div>
      <footer class="pir-footer">${esc(GENERATED_FOOTER_TEXT)}</footer>
    </main>
  `;
  wireEvents(page);
}

function syncIdentity(field: keyof BillingIdentitySnapshot, value: string): void {
  identityDraft = { ...identityDraft, [field]: value };
}

function wireEvents(page: HTMLElement): void {
  page.querySelectorAll<HTMLInputElement | HTMLSelectElement>('[data-identity-field]').forEach(input => {
    input.addEventListener('input', () => syncIdentity(input.dataset['identityField'] as keyof BillingIdentitySnapshot, input.value));
    input.addEventListener('change', () => {
      syncIdentity(input.dataset['identityField'] as keyof BillingIdentitySnapshot, input.value);
      renderPage(page);
    });
  });
  page.querySelectorAll<HTMLInputElement>('[data-scope]').forEach(input => {
    input.addEventListener('change', () => {
      const scope = input.dataset['scope'];
      if (scope === 'invoice-only' || scope === 'profile-future' || scope === 'both') applyScope = scope;
      renderPage(page);
    });
  });
  page.querySelector<HTMLButtonElement>('#pir-save-identity')?.addEventListener('click', () => {
    if (applyScope === 'invoice-only' || applyScope === 'both') saveReviewDraft();
    if (applyScope === 'profile-future' || applyScope === 'both') applyIdentityToProviderProfile();
    addAudit('Identity updated', `Billing identity saved with scope: ${applyScope}.`);
    noticeText = 'Billing identity changes saved.';
    renderPage(page);
  });
  page.querySelector<HTMLButtonElement>('#pir-confirm')?.addEventListener('click', () => {
    reviewDraft.providerReviewStatus = 'provider-confirmed';
    reviewDraft.providerConfirmedAt = new Date().toISOString();
    saveReviewDraft();
    addAudit('Invoice confirmed', `Invoice ${reviewDraft.invoiceNumber || 'draft'} marked provider-confirmed.`);
    noticeText = 'Invoice details confirmed.';
    renderPage(page);
  });
  page.querySelector<HTMLButtonElement>('#pir-report')?.addEventListener('click', () => {
    issuePanelOpen = !issuePanelOpen;
    renderPage(page);
  });
  page.querySelector<HTMLSelectElement>('#pir-issue-category')?.addEventListener('change', event => {
    issueCategory = (event.target as HTMLSelectElement).value;
  });
  page.querySelector<HTMLTextAreaElement>('#pir-issue-description')?.addEventListener('input', event => {
    issueDescription = (event.target as HTMLTextAreaElement).value;
  });
  page.querySelector<HTMLButtonElement>('#pir-submit-issue')?.addEventListener('click', () => {
    addAudit('Issue reported', `${issueCategory}: ${issueDescription || 'No description entered.'}`);
    issueDescription = '';
    issuePanelOpen = false;
    noticeText = 'Issue saved to mock audit log.';
    renderPage(page);
  });
}

export function isProviderInvoiceReviewPath(): boolean {
  return window.location.pathname.includes('provider-invoice-review');
}

export function renderProviderInvoiceReview(): void {
  reviewDraft = readInvoice();
  identityDraft = snapshotFromInvoice(reviewDraft);
  applyScope = 'invoice-only';
  issuePanelOpen = false;
  issueCategory = 'provider details wrong';
  issueDescription = '';
  noticeText = '';
  ensureAuditSeed();
  saveReviewDraft();
  renderPage(shell());
}
